import React, { useEffect, useState } from "react";
import { getCountryData } from "@stadium/ui/src/helpers/country-dropdown";

import CountryFlag from "@stadium/ui/src/shared/country-flag";
import SelectDropdown from "@stadium/ui/src/components/select-dropdown/select-dropdown";
import countryDD from "../../assets/stylesheets/core/dropdown/countryDropdown.module.scss";

import { useWeglot } from "@stadium/services/weglot/provider";
import { DEFAULT_LANG_OBJ } from "@stadium/services/weglot/constants";

const DEFAULT_LANG_KEY = DEFAULT_LANG_OBJ.key;

interface CountryProps {
  name: string;
  iso: string;
  id: number;
}

interface CountrySelectProps {
  selectedCountry: CountryProps;
  countries: CountryProps[];
  setSelectedCountry: (country: CountryProps) => void;
  type: string;
  labelText?: string;
  flagWidth?: number;
  flagHeight?: number;
  showSearchFilter?: boolean;
  hideCountryFlag?: boolean;
  customClassName?: string;
}

const CountrySelect = (props: CountrySelectProps) => {
  const {
    countries,
    selectedCountry,
    setSelectedCountry,
    type,
    labelText = "Country",
    flagWidth,
    flagHeight,
    showSearchFilter = true,
    customClassName = "",
    hideCountryFlag = false,
  } = props;

  const { batchGetTranslation, initialized, currentLang } = useWeglot();
  const shouldTranslate = initialized && currentLang !== DEFAULT_LANG_KEY;

  const [translationCache, setTranslationCache] = useState<{
    lang: string;
    names: Record<string, string>;
  }>({ lang: "", names: {} });

  const translatedNames =
    shouldTranslate && translationCache.lang === currentLang ? translationCache.names : {};

  useEffect(() => {
    if (!shouldTranslate || !countries?.length || !batchGetTranslation) return;

    let cancelled = false;

    const translateAll = async () => {
      const names = countries.map(({ name }) => name);
      const translated = await batchGetTranslation({
        texts: names,
        from: DEFAULT_LANG_KEY,
        to: currentLang,
      });
      if (!cancelled) {
        setTranslationCache({
          lang: currentLang,
          names: Object.fromEntries(
            countries.map(({ iso, name }, i) => [iso, translated[i] ?? name]),
          ),
        });
      }
    };

    translateAll();

    return () => {
      cancelled = true;
    };
  }, [shouldTranslate, currentLang, countries, batchGetTranslation]);

  const getCountryText = (countryObj?: CountryProps) => {
    const { iso, name } = countryObj ?? {};
    if (iso) {
      const displayName = shouldTranslate && translatedNames[iso] ? translatedNames[iso] : name;
      return (
        <div className={`btn-flex d-flex align-items-center px-4`}>
          {!hideCountryFlag && (
            <div className="flag-image-container">
              <CountryFlag
                key={iso}
                country={countryObj}
                type={type}
                className="img-flag h-100"
                flagWidth={flagWidth}
                flagHeight={flagHeight}
              />
            </div>
          )}
          <div
            className={`text-truncate fs-10 lh-1-2 ls-1 country-label ${countryDD.countryLabel} ${
              hideCountryFlag ? "country-label-without-flag" : "country-label-with-flag"
            }`}
          >
            {displayName}
          </div>
        </div>
      );
    }
    return "";
  };

  const countriesForDropdown =
    shouldTranslate && Object.keys(translatedNames).length
      ? countries.map((c) => (translatedNames[c.iso] ? { ...c, name: translatedNames[c.iso] } : c))
      : countries;

  const { countryMapping, countryOptions } = getCountryData(countriesForDropdown, {
    countryOptionParams: { getText: getCountryText },
  });

  const handleCountryChange = ({ value: countryId }) => {
    if (countryId && selectedCountry.iso != countryId) {
      setSelectedCountry(countryMapping[countryId]);
    }
  };

  return (
    <SelectDropdown
      key={currentLang}
      name="countryId"
      id="countryId"
      value={selectedCountry?.iso}
      options={countryOptions}
      text={getCountryText(countryMapping[selectedCountry?.iso])}
      onSelect={handleCountryChange}
      label={
        labelText && {
          for: "countryId",
          content: labelText,
          classes: "fs-12 lh-1-3 ls-1 text-uppercase font-weight-bold clr-052 label-CC",
        }
      }
      placeholder="United States"
      classes={`country-dropdown w-100 ${customClassName}`}
      showSearchFilter={showSearchFilter}
      searchfilterOptions={{
        placeholder: "Search",
        attributes: ["iso", "name"],
      }}
    />
  );
};

export default CountrySelect;
